import React, { useState, useEffect, useRef } from 'react'
import { fetchAutocomplete } from '../../api/fetchAutocomplete'
import { AUTOCOMPLETE_MIN_VALUE_LENGTH, AUTOCOMPLETE_DELAY_TIME } from '../../consts/data'

const DestinationsApartmentFilter = ({
    currentData,
    placeholder,
    toggleFilterMode,
    onChange,
    resetTrigger,
    onHotelsChange,
    onHotelsLoading
}) => {
    const [inputValue, setInputValue] = useState('')
    const [errorMessage, setErrorMessage] = useState(null)
    const debounceRef = useRef(null)
    const latestQueryRef = useRef('')

    useEffect(() => {
        setInputValue('')
        setErrorMessage(null)
        toggleFilterMode(false)
        latestQueryRef.current = ''
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        if (typeof onHotelsChange === 'function') onHotelsChange([])
        if (typeof onHotelsLoading === 'function') onHotelsLoading(false)
    }, [resetTrigger])

    const filterHandler = (event) => {
        const query = event.target.value.toLowerCase();

        setInputValue(event.target.value)
        latestQueryRef.current = query

        if (query.length > 0 && query.length < AUTOCOMPLETE_MIN_VALUE_LENGTH) {
            setErrorMessage('Minimum 3 znaki')
            toggleFilterMode(false)
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
            if (typeof onHotelsChange === 'function') onHotelsChange([])
            return
        }

        if (query.length === 0) {
            setInputValue('')
            setErrorMessage(null)
            toggleFilterMode(false)
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
            if (typeof onHotelsChange === 'function') onHotelsChange([])
            return
        }

        setErrorMessage(null)
        toggleFilterMode(true)

        const t = filterRegionsBySearch(currentData, query)
        onChange(t)

        if (debounceRef.current) clearTimeout(debounceRef.current)
        const fetchQuery = query
        debounceRef.current = setTimeout(async () => {
            try {
                if (typeof onHotelsLoading === 'function') onHotelsLoading(true)
                const response = await fetchAutocomplete(fetchQuery)
                // Ignoruj spóźnione odpowiedzi, jeśli zapytanie się zmieniło
                if (latestQueryRef.current !== fetchQuery) {
                    return
                }
                if (response && response.status === true && Array.isArray(response.list)) {
                    if (typeof onHotelsChange === 'function') onHotelsChange(response.list)
                } else {
                    if (typeof onHotelsChange === 'function') onHotelsChange([])
                }
            } catch (e) {
                if (typeof onHotelsChange === 'function') onHotelsChange([])
            } finally {
                if (typeof onHotelsLoading === 'function') onHotelsLoading(false)
            }
        }, AUTOCOMPLETE_DELAY_TIME)
    }

    const filterRegionsBySearch = (data, searchTerm) => {
        const result = {};
        const lowerSearch = searchTerm.toLowerCase();

        // Sprawdź czy data istnieje i nie jest pusty
        if (!data || typeof data !== 'object') {
            return result;
        }

        for (const [country, regions] of Object.entries(data)) {
            const countryMatches = country.toLowerCase().includes(lowerSearch);

            // Sprawdź czy regions jest tablicą (stara struktura) czy obiektem (nowa struktura z 3 poziomem)
            if (Array.isArray(regions)) {
                // Stara struktura: kraj -> [region1, region2, ...]
                const matchingRegions = regions.filter(region =>
                    region.toLowerCase().includes(lowerSearch)
                );

                if (countryMatches) {
                    // Jeśli kraj pasuje, pokaż wszystkie regiony (lub tylko pasujące jeśli są)
                    result[country] = matchingRegions.length > 0 ? matchingRegions : regions;
                } else if (matchingRegions.length > 0) {
                    // Jeśli tylko regiony pasują, pokaż tylko pasujące
                    result[country] = matchingRegions;
                }
            } else if (regions && typeof regions === 'object' && !Array.isArray(regions)) {
                // Nowa struktura: kraj -> {region: [miasto1, miasto2, ...]}
                const matchingRegions = {};
                let hasMatches = false;

                for (const [region, cities] of Object.entries(regions)) {
                    const regionMatches = region.toLowerCase().includes(lowerSearch);
                    
                    if (Array.isArray(cities)) {
                        // Sprawdź miasta (trzeci poziom)
                        const matchingCities = cities.filter(city =>
                            city.toLowerCase().includes(lowerSearch)
                        );

                        if (regionMatches) {
                            // Jeśli region pasuje, dodaj wszystkie miasta tego regionu
                            matchingRegions[region] = cities;
                            hasMatches = true;
                        } else if (matchingCities.length > 0) {
                            // Jeśli tylko miasta pasują, dodaj tylko pasujące miasta
                            matchingRegions[region] = matchingCities;
                            hasMatches = true;
                        }
                    } else if (regionMatches) {
                        // Jeśli region pasuje ale nie ma miast (lub cities nie jest tablicą)
                        matchingRegions[region] = cities;
                        hasMatches = true;
                    }
                }

                if (countryMatches) {
                    // Jeśli kraj pasuje, pokaż wszystkie regiony (lub tylko pasujące jeśli są)
                    result[country] = hasMatches ? matchingRegions : regions;
                } else if (hasMatches) {
                    // Jeśli tylko regiony/miasta pasują, pokaż tylko pasujące
                    result[country] = matchingRegions;
                }
            }
        }

        return result;
    }

    return (
        <div className="destinations-filter">
            <input
                type="search"
                placeholder={placeholder}
                value={inputValue}
                aria-describedby="basic-addon1"
                onChange={filterHandler}
            />
            {errorMessage && <span>{errorMessage}</span>}
        </div>
    )
}

export default DestinationsApartmentFilter;
